import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    LinearProgress,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useSnackbar } from 'notistack';
import SearchableSelect from '../SearchableSelect';
import { getApiErrorMessage } from '../../utils/apiError';
import { eliminarConfiguracionLlm, guardarConfiguracionLlm, obtenerConfiguracionLlm } from '../../services/consultasIaService';

interface ProveedorLlm {
    key: string;
    label: string;
    defaultModel: string;
}

const PROVEEDORES: ProveedorLlm[] = [
    { key: 'openai', label: 'OpenAI', defaultModel: 'gpt-4o-mini' },
    { key: 'anthropic', label: 'Anthropic (Claude)', defaultModel: 'claude-sonnet-5-5' },
    { key: 'gemini', label: 'Google Gemini', defaultModel: 'gemini-3.8-flash' },
    { key: 'xai', label: 'xAI (Grok)', defaultModel: 'grok-4.7' },
    { key: 'perplexity', label: 'Perplexity', defaultModel: 'sonar' },
    { key: 'deepseek', label: 'DeepSeek', defaultModel: 'deepseek-chat' },
    { key: 'groq', label: 'Groq', defaultModel: 'llama-3.3-70b-versatile' },
    { key: 'mistral', label: 'Mistral AI', defaultModel: 'mistral-small-latest' },
    { key: 'together', label: 'Together AI', defaultModel: 'meta-llama/Llama-3.3-70B-Instruct-Turbo' },
    { key: 'openrouter', label: 'OpenRouter', defaultModel: 'openai/gpt-4o-mini' },
    { key: 'ollama', label: 'Ollama (local)', defaultModel: 'llama3.2' },
    { key: 'custom', label: 'Personalizado (URL compatible OpenAI)', defaultModel: '' },
];

const proveedorPorClave = (clave: string): ProveedorLlm =>
    PROVEEDORES.find((p) => p.key === clave) ?? PROVEEDORES[0];

interface ConsultaIaLlmConfigDialogProps {
    open: boolean;
    onClose: () => void;
}

const ConsultaIaLlmConfigDialog: React.FC<ConsultaIaLlmConfigDialogProps> = ({ open, onClose }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [hayApiKey, setHayApiKey] = useState(false);
    const [provider, setProvider] = useState('openai');
    const [model, setModel] = useState('');
    const [baseUrl, setBaseUrl] = useState('');
    const [apiKey, setApiKey] = useState('');
    const [mostrarApiKey, setMostrarApiKey] = useState(false);
    const [error, setError] = useState('');
    const [ayudaAbierta, setAyudaAbierta] = useState(false);
    const [confirmarLimpieza, setConfirmarLimpieza] = useState(false);

    const proveedor = useMemo(() => proveedorPorClave(provider), [provider]);

    const cargar = useCallback(async () => {
        setLoading(true);
        setError('');
        setApiKey('');
        setMostrarApiKey(false);
        setConfirmarLimpieza(false);
        try {
            const config = await obtenerConfiguracionLlm();
            setHayApiKey(config.tieneApiKey);
            const clave = config.provider.trim().toLowerCase();
            setProvider(PROVEEDORES.some((p) => p.key === clave) ? clave : 'openai');
            setModel(config.model.trim());
            const url = config.baseUrl.trim();
            setBaseUrl(url === '-' ? '' : url);
        } catch (err: unknown) {
            const status = (err as { response?: { status?: number } })?.response?.status;
            const mensaje = status === 403
                ? 'No tiene permisos para ver la configuración de IA.'
                : 'No se pudo cargar la configuración de IA.';
            setError(mensaje);
            enqueueSnackbar(mensaje, { variant: 'error' });
        } finally {
            setLoading(false);
        }
    }, [enqueueSnackbar]);

    useEffect(() => {
        if (!open) return;
        void cargar();
    }, [open, cargar]);

    const guardar = async () => {
        const providerKey = (provider || 'openai').trim().toLowerCase();
        const modelValor = model.trim();
        const baseUrlValor = baseUrl.trim();
        const apiKeyValor = apiKey.trim();

        if (providerKey === 'custom' && !baseUrlValor) {
            setError('Indicá la URL base del proveedor personalizado.');
            return;
        }
        if (!hayApiKey && !apiKeyValor) {
            setError('La API key es obligatoria.');
            return;
        }

        setSaving(true);
        setError('');
        try {
            await guardarConfiguracionLlm({
                provider: providerKey,
                model: modelValor,
                baseUrl: baseUrlValor,
                apiKey: apiKeyValor || undefined,
            });
            enqueueSnackbar('Configuración de IA guardada', { variant: 'success' });
            onClose();
        } catch (err: unknown) {
            const status = (err as { response?: { status?: number } })?.response?.status;
            const mensaje = status === 403
                ? 'No tiene permisos para modificar la configuración de IA.'
                : getApiErrorMessage(err, 'No se pudo guardar la configuración de IA.');
            setError(mensaje);
            enqueueSnackbar(mensaje, { variant: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const limpiar = async () => {
        setSaving(true);
        setError('');
        try {
            await eliminarConfiguracionLlm();
            setHayApiKey(false);
            setProvider('openai');
            setModel('');
            setBaseUrl('');
            setApiKey('');
            setConfirmarLimpieza(false);
            enqueueSnackbar('Se quitó la configuración de IA', { variant: 'success' });
        } catch (err: unknown) {
            const status = (err as { response?: { status?: number } })?.response?.status;
            const mensaje = status === 403
                ? 'No tiene permisos para quitar la configuración de IA.'
                : getApiErrorMessage(err, 'No se pudo quitar la configuración de IA.');
            setError(mensaje);
            enqueueSnackbar(mensaje, { variant: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const ayudaModelo = proveedor.defaultModel
        ? `Si lo dejás vacío se usa ${proveedor.defaultModel}.`
        : 'Indicá el nombre del modelo del proveedor.';

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ pr: 1.5 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    <span>Configuración de IA</span>
                    <Tooltip title={ayudaAbierta ? 'Ocultar ayuda' : 'Ayuda'}>
                        <IconButton
                            aria-label="Ayuda de la configuración de IA"
                            onClick={() => setAyudaAbierta((v) => !v)}
                            color={ayudaAbierta ? 'primary' : 'default'}
                        >
                            <HelpOutlineIcon />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2} sx={{ pt: 0.5 }}>
                    <Collapse in={ayudaAbierta}>
                        <Alert severity="info" icon={false}>
                            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Cómo funciona</Typography>
                            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                                <li>La clave es de la empresa y se usa en Ventas, Compras, Inventario y Tesorería.</li>
                                <li>Elegí el proveedor y pegá la API key. Si el modelo queda vacío, se usa el sugerido.</li>
                                <li>La clave no se vuelve a mostrar. Si la dejás vacía al guardar, se conserva la actual.</li>
                                <li>Si el proveedor no está en la lista, elegí Personalizado y pegá la URL completa terminada en /chat/completions.</li>
                                <li>Quitar configuración borra la clave. Hasta cargar otra, las consultas quedan sin IA.</li>
                            </Box>
                        </Alert>
                    </Collapse>
                    {loading && <LinearProgress />}
                    {error && <Alert severity="error">{error}</Alert>}
                    <SearchableSelect<string>
                        label="Proveedor"
                        allowEmpty={false}
                        disabled={loading || saving}
                        value={provider}
                        onChange={(valor) => {
                            if (!valor) return;
                            setProvider(valor);
                        }}
                        options={PROVEEDORES.map((p) => ({ value: p.key, label: p.label }))}
                    />
                    <TextField
                        label="Modelo"
                        value={model}
                        onChange={(e) => setModel(e.target.value)}
                        fullWidth
                        disabled={loading || saving}
                        helperText={ayudaModelo}
                        inputProps={{ maxLength: 200 }}
                    />
                    <TextField
                        label="URL base"
                        value={baseUrl}
                        onChange={(e) => setBaseUrl(e.target.value)}
                        fullWidth
                        required={provider === 'custom'}
                        disabled={loading || saving}
                        placeholder="https://api.ejemplo.com/v1"
                        helperText="Obligatoria si el proveedor es personalizado. Si termina en /v1, se completa con /chat/completions."
                        inputProps={{ maxLength: 500 }}
                    />
                    <TextField
                        label="API key"
                        type={mostrarApiKey ? 'text' : 'password'}
                        value={apiKey}
                        onChange={(e) => setApiKey(e.target.value)}
                        fullWidth
                        required={!hayApiKey}
                        disabled={loading || saving}
                        autoComplete="new-password"
                        helperText={
                            hayApiKey
                                ? 'Ya hay una clave cargada. Dejala vacía para conservarla.'
                                : 'Clave del proveedor. Queda guardada para esta empresa.'
                        }
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label={mostrarApiKey ? 'Ocultar API key' : 'Mostrar API key'}
                                        onClick={() => setMostrarApiKey((v) => !v)}
                                        edge="end"
                                        disabled={loading || saving}
                                    >
                                        {mostrarApiKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    {confirmarLimpieza && (
                        <Alert
                            severity="warning"
                            action={
                                <Stack direction="row" spacing={1}>
                                    <Button color="inherit" size="small" disabled={saving} onClick={() => setConfirmarLimpieza(false)}>
                                        Cancelar
                                    </Button>
                                    <Button color="error" size="small" disabled={saving} onClick={() => void limpiar()}>
                                        Quitar
                                    </Button>
                                </Stack>
                            }
                        >
                            Se borra la clave y el proveedor de esta empresa. Las consultas dejan de funcionar hasta cargar una nueva.
                        </Alert>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
                <Box>
                    {hayApiKey && (
                        <Button
                            color="error"
                            disabled={loading || saving || confirmarLimpieza}
                            onClick={() => setConfirmarLimpieza(true)}
                        >
                            Quitar configuración
                        </Button>
                    )}
                </Box>
                <Stack direction="row" spacing={1}>
                    <Button onClick={onClose} disabled={saving}>
                        Cancelar
                    </Button>
                    <Button variant="contained" onClick={() => void guardar()} disabled={loading || saving}>
                        Guardar
                    </Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );
};

export default ConsultaIaLlmConfigDialog;
